import React, { createContext, useState, useContext, useEffect } from 'react';
import { projectsAPI, authAPI } from '../services/api';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getProjects();
      const projectsList = response.data.projects || [];
      setProjects(projectsList);

      // Set current project from localStorage or use first project
      const savedProjectId = localStorage.getItem('currentProjectId');
      
      if (savedProjectId && projectsList.find(p => p._id === savedProjectId)) {
        const project = projectsList.find(p => p._id === savedProjectId);
        setCurrentProject(project);
      } else if (projectsList.length > 0) {
        setCurrentProject(projectsList[0]);
        localStorage.setItem('currentProjectId', projectsList[0]._id);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchProject = async (projectId) => {
    const project = projects.find(p => p._id === projectId);
    if (project) {
      try {
        // Call API to get new tokens with updated project_id
        const response = await authAPI.switchProject(projectId);
        const { access_token, refresh_token } = response.data;
        
        // Update tokens in localStorage
        localStorage.setItem('token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('currentProjectId', projectId);
        
        // Update React state
        setCurrentProject(project);
        
        // Trigger a custom event to notify other components to refresh their data
        window.dispatchEvent(new CustomEvent('projectSwitched', { 
          detail: { projectId, project } 
        }));
        
        // Reload page to ensure all data is refreshed with new project context
        window.location.reload();
        
        console.log('Successfully switched to project:', project.name);
      } catch (error) {
        console.error('Failed to switch project:', error);
        // Show error notification if available
        if (window.showError) {
          window.showError(error.response?.data?.message || 'Failed to switch project');
        }
      }
    }
  };

  const createProject = async (name, description) => {
    try {
      const response = await projectsAPI.createProject({ name, description });
      await loadProjects(); // Reload projects list
      return response.data;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await projectsAPI.deleteProject(projectId);
      await loadProjects(); // Reload projects list
      
      // If deleted project was current, switch to first available
      if (currentProject && currentProject._id === projectId) {
        if (projects.length > 1) {
          const remainingProjects = projects.filter(p => p._id !== projectId);
          switchProject(remainingProjects[0]._id);
        } else {
          setCurrentProject(null);
          localStorage.removeItem('currentProjectId');
        }
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  };

  const value = {
    currentProject,
    projects,
    loading,
    switchProject,
    createProject,
    deleteProject,
    refreshProjects: loadProjects
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;

